import { groupBy } from "lodash";
import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { Button, Divider, HStack, Stack, Text } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button/button";
import { cn } from "@/utils/cn";
import { shouldUseWhiteText } from "@/utils/colors";

import {
  isThreeWayDrawContract,
  sortPredictionContracts,
} from "./prediction-contract-order";

import type { PredictionContract, PredictionStatus } from "@/schemas";
import type { Game } from "@/schemas/prediction";

const messages = defineMessages({
  more: { id: "prediction.contracts.more", defaultMessage: "+{value} more" },
  seeMore: { id: "prediction.contracts.seeMore", defaultMessage: "See More" },
  seeLess: { id: "prediction.contracts.seeLess", defaultMessage: "See Less" },
});

type CardLayout = "timeline" | "featured" | "detail";
type ContractLayout =
  | "default"
  | "teamGame"
  | "threeWayGame"
  | "individualMatch";

type PredictionContractsProps = {
  status: PredictionStatus;
  layout?: CardLayout;
};

const TEAM_GAME_CONTRACT_TYPES = ["moneyline", "spread", "puck line"];
const MAIN_GAME_CONTRACT_TYPES = ["Moneyline", "Moneyline (3-Way)"];
const MAX_COLLAPSED_CONTRACTS = 5;

const getProbabilityPercent = (value: string | null): number => {
  if (!value) return 0;
  const parsed = parseFloat(value) * 100;
  if (Number.isNaN(parsed)) return 0;
  return Math.min(Math.max(parsed, 0), 100);
};

const formatProbability = (value: string | null): string =>
  getProbabilityPercent(value).toFixed(0);

function getContractGameDetails(
  contract: PredictionContract,
  game: Game | null,
) {
  if (!game) return null;

  const { title } = contract;
  if ("home_team" in game.data) {
    if (
      game.data.home_team.name.length > 0 &&
      title.includes(game.data.home_team.name)
    )
      return game.data.home_team;
    if (
      game.data.away_team.name.length > 0 &&
      title.includes(game.data.away_team.name)
    )
      return game.data.away_team;
  }
  if ("fighter_1" in game.data) {
    if (
      game.data.fighter_1.name.length > 0 &&
      title.includes(game.data.fighter_1.name)
    )
      return game.data.fighter_1;
    if (
      game.data.fighter_2.name.length > 0 &&
      title.includes(game.data.fighter_2.name)
    )
      return game.data.fighter_2;
  }
  if (
    "leader" in game.data &&
    game.data.leader.length > 0 &&
    title.includes(game.data.leader)
  )
    return game;

  return null;
}

function getContractLiveResult(
  contract: PredictionContract,
  game: Game | null,
) {
  const details = getContractGameDetails(contract, game);
  if (!game || !details || (!game.data.is_live && !game.data.is_over))
    return null;

  if ("score" in details) return details.score;
  if ("is_winner" in details && "result" in game.data) return game.data.result;
  if ("leader" in details) return "Leader";
  return null;
}

export function getPredictionCardLayout(
  predictionType?: string | null,
  contractType?: string | null,
): ContractLayout {
  const type = predictionType?.trim().toLowerCase();
  const contract = contractType?.trim().toLowerCase();

  if (type === "fight" && contract === "moneyline") {
    return "individualMatch";
  }

  if (type === "game") {
    if (contract === "moneyline (3-way)") {
      return "threeWayGame";
    }
    if (contract && TEAM_GAME_CONTRACT_TYPES.includes(contract)) {
      return "teamGame";
    }
  }

  return "default";
}

export default function PredictionContracts({
  status,
  layout = "timeline",
}: PredictionContractsProps) {
  const intl = useIntl();
  const { prediction } = status;

  if (layout === "detail") {
    return (
      <Stack space={2} className="h-full">
        <GroupedContractsLayout
          contracts={prediction.contracts}
          game={status.sporting_event}
          showProbabilityBar
        />
      </Stack>
    );
  }

  const firstContractType = prediction.contracts[0]?.type ?? null;
  const cardLayout = getPredictionCardLayout(
    prediction.type,
    firstContractType,
  );
  const maxContracts = cardLayout === "threeWayGame" ? 3 : 2;
  const contracts = sortPredictionContracts(prediction.contracts).slice(
    0,
    maxContracts,
  );

  const hasOverflow = prediction.contracts.length > maxContracts;
  const overflowButtonStyles = cn(
    buttonVariants({ variant: "outline" }),
    "py-1",
  );

  return (
    <Stack space={2} className="h-full">
      <Stack space={layout === "featured" ? 4 : 0}>
        {contracts.map((contract) =>
          layout === "featured" ? (
            <FeaturedContract key={contract.id} contract={contract} />
          ) : (
            <ContractRow
              key={contract.id}
              contract={contract}
              game={status.sporting_event}
              showLiveResult
            />
          ),
        )}
      </Stack>

      {hasOverflow && (
        <div className={overflowButtonStyles}>
          {intl.formatMessage(messages.more, {
            value: prediction.contracts.length - maxContracts,
          })}
        </div>
      )}
    </Stack>
  );
}

const ContractGameIcon = ({ contract }: { contract: PredictionContract }) => {
  const bgColor = contract.icon_colors[0]
    ? `#${contract.icon_colors[0].light_background}`
    : undefined;
  const isDraw = isThreeWayDrawContract(contract);
  const abbrev = isDraw ? "D" : (contract.icon_abbrev?.slice(0, 3) ?? null);
  const isMainGameContract = MAIN_GAME_CONTRACT_TYPES.includes(
    contract.type ?? "",
  );

  if (!abbrev || !isMainGameContract) return null;

  return (
    <div
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-lg",
        {
          "bg-gray-200 dark:bg-gray-800": !bgColor,
        },
      )}
      style={{ backgroundColor: bgColor }}
    >
      <Text
        size="xs"
        weight="bold"
        className={cn({
          "text-gray-900": !shouldUseWhiteText(bgColor),
          "text-white": shouldUseWhiteText(bgColor),
        })}
      >
        {abbrev}
      </Text>
    </div>
  );
};

const FeaturedContract = ({ contract }: { contract: PredictionContract }) => (
  <HStack
    justifyContent="between"
    alignItems="center"
    className="relative rounded-lg border bg-background px-4 py-3"
  >
    <div
      className="absolute top-0 left-0 h-full rounded-l-lg bg-placeholder"
      style={{ width: `${formatProbability(contract.yes)}%` }}
    />
    <Text size="lg" className="z-10" truncate align="left">
      {contract.title}
    </Text>
    <Text size="lg" weight="bold" className="z-10">
      {formatProbability(contract.yes)}%
    </Text>
  </HStack>
);

const ContractRow = ({
  contract,
  game,
  showLiveResult = false,
  showProbabilityBar = false,
}: {
  contract: PredictionContract;
  game?: Game | null;
  showLiveResult?: boolean;
  showProbabilityBar?: boolean;
}) => (
  <Stack>
    <HStack
      space={4}
      justifyContent="between"
      alignItems="center"
      className="py-2"
    >
      <HStack
        space={3}
        alignItems="center"
        justifyContent="between"
        className="w-full min-w-0"
      >
        <HStack space={4} alignItems="center" className="w-full min-w-0">
          <ContractGameIcon contract={contract} />
          <Text size="md" truncate align="left">
            {contract.title}
          </Text>
        </HStack>

        {showLiveResult && (
          <Text size="md" weight="bold">
            {getContractLiveResult(contract, game ?? null)}
          </Text>
        )}
      </HStack>

      <Button
        variant="outline"
        className="w-20 border-1 border-primary hover:border-primary focus:border-primary dark:border-primary dark:hover:border-primary dark:focus:border-primary"
        href={contract.affiliate_url}
        target="_blank"
        onClick={(event) => {
          // Prevent the outer card from being clicked
          event.stopPropagation();
        }}
        style={{
          borderColor: contract.icon_colors[0]
            ? `#${contract.icon_colors[0].base}`
            : undefined,
        }}
      >
        <Text size="md" theme="muted" weight="medium">
          {formatProbability(contract.yes)}%
        </Text>
      </Button>
    </HStack>

    {showProbabilityBar && (
      <div
        className="h-1.5 w-full rounded-full bg-primary/20"
        style={{
          backgroundColor: contract.icon_colors[0]
            ? `#${contract.icon_colors[0].base}33`
            : undefined,
        }}
      >
        <div
          className="h-full rounded-full bg-primary"
          style={{
            width: `${getProbabilityPercent(contract.yes)}%`,
            backgroundColor: contract.icon_colors[0]
              ? `#${contract.icon_colors[0].base}`
              : undefined,
          }}
        />
      </div>
    )}
  </Stack>
);

const GroupedContracts = ({
  contracts,
  game,
  type,
  showProbabilityBar = false,
}: {
  contracts: PredictionContract[];
  game: Game | null;
  type: string;
  showProbabilityBar?: boolean;
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const isMainGameContract = MAIN_GAME_CONTRACT_TYPES.includes(type);
  const orderedContracts = sortPredictionContracts(contracts);
  const visibleContracts = expanded
    ? orderedContracts
    : orderedContracts.slice(0, MAX_COLLAPSED_CONTRACTS);

  return (
    <Stack space={isMainGameContract ? 0 : 2}>
      {!isMainGameContract && (
        <Text size="xl" weight="bold">
          {type}
        </Text>
      )}

      {visibleContracts.map((contract) => (
        <ContractRow
          key={contract.id}
          contract={contract}
          game={isMainGameContract ? game : null}
          showProbabilityBar={showProbabilityBar}
        />
      ))}

      {!isMainGameContract && contracts.length > MAX_COLLAPSED_CONTRACTS && (
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded
            ? intl.formatMessage(messages.seeLess)
            : intl.formatMessage(messages.seeMore)}
        </Button>
      )}
    </Stack>
  );
};

const GroupedContractsLayout = ({
  contracts,
  game,
  showProbabilityBar = false,
}: {
  contracts: PredictionContract[];
  game: Game | null;
  showProbabilityBar?: boolean;
}) => {
  const grouped = groupBy(contracts, "type");
  const types = Object.keys(grouped);

  if (types.length === 1 || types[0] === null) {
    return sortPredictionContracts(contracts).map((contract) => (
      <ContractRow
        key={contract.id}
        contract={contract}
        game={game}
        showProbabilityBar={showProbabilityBar}
      />
    ));
  }

  return types.map((type, index) => (
    <Stack key={type} space={6} className={cn({ "mt-6": index !== 0 })}>
      <GroupedContracts
        contracts={grouped[type]}
        game={game}
        type={type}
        showProbabilityBar={showProbabilityBar}
      />
      {index !== types.length - 1 && <Divider />}
    </Stack>
  ));
};
